/**
 * Company settings: identity, numbering policy, users and periods.
 *
 * The numbering panel is where the group code style is chosen. Switching
 * it is safe because group codes are display-only -- but it still shows a
 * full preview before writing, since seeing every old -> new code is what
 * makes an accountant comfortable pressing the button.
 */
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, MenuItem, Paper, Skeleton, Stack, Switch, Tab, Tabs, TextField,
  Typography,
} from '@mui/material'
import LockIcon from '@mui/icons-material/LockOutlined'
import LockOpenIcon from '@mui/icons-material/LockOpenOutlined'
import { useTranslation } from 'react-i18next'

import {
  fetchCompany, fetchPolicy, fetchStaff, fetchYears, lockYear,
  renumberGroups, updateCompany, updatePolicy,
} from '../api/endpoints'
import { errorMessage } from '../api/client'
import { P, type GroupStyle, type RenumberResult } from '../api/types'
import { useAuth } from '../store/AuthContext'
import { modulesOf, type ModuleName } from '../nav/menu'
import { c } from '../theme'
import StaffDialog from './StaffDialog'
import OpeningBalances from './OpeningBalances'
import CompaniesPanel from './CompaniesPanel'
import AuditLogPanel from './AuditLogPanel'
import ScreenTitle, { PanelHeading } from '../components/ScreenTitle'

const STYLES: { value: GroupStyle; example: string }[] = [
  { value: 'MNEMONIC', example: 'DEBTORS' },
  { value: 'DOTTED', example: '1000.01.03' },
  { value: 'NUMERIC', example: '1000103' },
]

export default function Settings() {
  const { t } = useTranslation()
  const { can, me } = useAuth()
  const qc = useQueryClient()
  // Named, not numbered: inserting the Companies tab would otherwise
  // have shifted every index after it and opened the wrong panel.
  type TabName = 'company' | 'companies' | 'modules' | 'numbering' | 'users'
    | 'periods' | 'opening' | 'audit'
  // ?tab=modules: the "module switched off" page links straight here.
  const [params] = useSearchParams()
  const [tab, setTab] = useState<TabName>(
    (params.get('tab') as TabName | null) ?? 'company')

  const isAdmin = can(P.adminCompany)
  const canUsers = can(P.adminUsers)
  const canPeriods = can(P.adminPeriods)

  return (
    <Paper variant="outlined">
      <ScreenTitle titleKey="nav.settings" sub={me?.companies.find(
        (x) => x.id === me.company_id)?.name_en} />
      <Tabs value={tab} onChange={(_, v) => setTab(v)}
        variant="scrollable" allowScrollButtonsMobile
        sx={{ px: 1.5, borderBottom: `1px solid ${c.border}`, minHeight: 40 }}>
        <Tab value="company" label={t('settings.company')} sx={{ minHeight: 40 }} />
        {/* Hidden rather than refused: creating companies is an admin
            job, and a tab that only ever says "no access" is clutter. */}
        {isAdmin && (
          <Tab value="companies" label={t('settings.companies')}
            sx={{ minHeight: 40 }} />
        )}
        <Tab value="modules" label={t('modules.tab')} sx={{ minHeight: 40 }} />
        <Tab value="numbering" label={t('settings.numbering')} sx={{ minHeight: 40 }} />
        <Tab value="users" label={t('settings.users')} sx={{ minHeight: 40 }} />
        <Tab value="periods" label={t('settings.periods')} sx={{ minHeight: 40 }} />
        <Tab value="opening" label={t('settings.opening')} sx={{ minHeight: 40 }} />
        {canUsers && (
          <Tab value="audit" label={t('settings.audit')} sx={{ minHeight: 40 }} />
        )}
      </Tabs>

      <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
        {tab === 'company' && <CompanyPanel editable={isAdmin} />}
        {tab === 'companies' && isAdmin && <CompaniesPanel />}
        {tab === 'modules' && <ModulesPanel editable={isAdmin} />}
        {tab === 'numbering' && <NumberingPanel editable={isAdmin} onDone={() =>
          qc.invalidateQueries({ queryKey: ['account-tree'] })} />}
        {tab === 'users' && (canUsers ? <UsersPanel /> :
          <Alert severity="warning">{t('settings.noAccess')}</Alert>)}
        {tab === 'periods' && (canPeriods ? <PeriodsPanel /> :
          <Alert severity="warning">{t('settings.noAccess')}</Alert>)}
        {tab === 'opening' && <OpeningBalances />}
        {tab === 'audit' && canUsers && <AuditLogPanel />}
      </Box>
    </Paper>
  )
}

// --- company ---------------------------------------------------------------

export function CompanyPanel({ editable }: { editable: boolean }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { refreshMe } = useAuth()
  const q = useQuery({ queryKey: ['company'], queryFn: fetchCompany })
  const [form, setForm] = useState<Record<string, string>>({})
  const [msg, setMsg] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: () => updateCompany(form),
    onSuccess: () => {
      setMsg(t('settings.saved')); setError(null); setForm({})
      void qc.invalidateQueries({ queryKey: ['company'] })
      // Trip screens read Way Bill / Weight from the session, so a change
      // here must reach them without a reload.
      if ('trip_ref_column' in form) void refreshMe()
    },
    onError: (e) => setError(errorMessage(e)),
  })

  if (q.isLoading) return <><Skeleton /><Skeleton /></>
  const d = q.data!
  const v = (k: string) => form[k] ?? ((d as never)[k] ?? '')
  const set = (k: string, val: string) => setForm({ ...form, [k]: val })

  /** Logos are stored inline as a data URL so printing needs no network. */
  function onLogo(file: File) {
    if (file.size > 250_000) {
      setError(t('settings.logoTooLarge')); return
    }
    const r = new FileReader()
    r.onload = () => set('logo', String(r.result))
    r.readAsDataURL(file)
  }

  return (
    <Stack spacing={2.5} sx={{ maxWidth: 620 }}>
      {msg && <Alert severity="success" onClose={() => setMsg(null)}>{msg}</Alert>}
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      <PanelHeading>{t('settings.identity')}</PanelHeading>
      <TextField label={t('settings.companyName')} value={v('name_en')}
        disabled={!editable} onChange={(e) => set('name_en', e.target.value)} />
      <TextField label={`${t('settings.companyName')} (العربية)`} value={v('name_ar')}
        disabled={!editable} onChange={(e) => set('name_ar', e.target.value)} />
      <TextField label={t('settings.legalName')} value={v('legal_name')}
        disabled={!editable} helperText={t('settings.legalNameHint')}
        onChange={(e) => set('legal_name', e.target.value)} />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField label={t('settings.vatNumber')} value={v('vat_number')}
          disabled={!editable} helperText={t('settings.vatHint')} sx={{ flex: 1 }}
          onChange={(e) => set('vat_number', e.target.value)} />
        <TextField label={t('settings.crNumber')} value={v('cr_number')}
          disabled={!editable} helperText={t('settings.crHint')} sx={{ flex: 1 }}
          onChange={(e) => set('cr_number', e.target.value)} />
      </Stack>

      <PanelHeading hint={t('settings.letterheadHint')}>
        {t('settings.letterhead')}</PanelHeading>

      <TextField label={t('settings.address')} value={v('address_en')}
        disabled={!editable} multiline minRows={2}
        onChange={(e) => set('address_en', e.target.value)} />
      <TextField label={`${t('settings.address')} (العربية)`} value={v('address_ar')}
        disabled={!editable} multiline minRows={2}
        onChange={(e) => set('address_ar', e.target.value)} />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField label={t('settings.phone')} value={v('phone')}
          disabled={!editable} sx={{ flex: 1 }}
          onChange={(e) => set('phone', e.target.value)} />
        <TextField label={t('settings.email')} value={v('email')}
          disabled={!editable} sx={{ flex: 1 }}
          onChange={(e) => set('email', e.target.value)} />
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center">
        {v('logo') ? (
          <Box component="img" src={v('logo')} alt=""
            sx={{ height: 56, maxWidth: 180, objectFit: 'contain',
                  border: `1px solid ${c.border}`, borderRadius: 1, p: 0.5 }} />
        ) : (
          <Box sx={{ height: 56, width: 120, display: 'grid',
            placeItems: 'center', border: `1px dashed ${c.border}`,
            borderRadius: 1, color: c.inkMuted, fontSize: 12 }}>
            {t('settings.noLogo')}
          </Box>
        )}
        {editable && (
          <Stack spacing={0.5}>
            <Button component="label" size="small" variant="outlined">
              {t('settings.uploadLogo')}
              <input type="file" hidden accept="image/png,image/jpeg,image/svg+xml"
                onChange={(e) => e.target.files?.[0] && onLogo(e.target.files[0])} />
            </Button>
            {v('logo') && (
              <Button size="small" color="inherit" sx={{ color: c.inkMuted }}
                onClick={() => set('logo', '')}>{t('settings.removeLogo')}</Button>
            )}
          </Stack>
        )}
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
        {t('settings.bannerHint')}
      </Typography>

      <PanelHeading hint={t('settings.taxInvoiceHint')}>
        {t('settings.taxInvoice')}</PanelHeading>

      <TextField select label={t('settings.tripRefColumn')}
        value={v('trip_ref_column') || 'WAY_BILL'} disabled={!editable}
        helperText={t('settings.tripRefHint')}
        onChange={(e) => set('trip_ref_column', e.target.value)}>
        <MenuItem value="WAY_BILL">{t('trip.wayBill')}</MenuItem>
        <MenuItem value="WEIGHT">{t('trip.weightKg')}</MenuItem>
      </TextField>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField label={t('settings.invoicePrefix')} value={v('invoice_prefix')}
          disabled={!editable} sx={{ flex: 1 }}
          inputProps={{ maxLength: 10, style: { textTransform: 'uppercase' } }}
          onChange={(e) => set('invoice_prefix', e.target.value.toUpperCase())} />
        <TextField label={t('settings.invoiceNextNo')} type="number"
          value={v('invoice_next_no')} disabled={!editable} sx={{ flex: 1 }}
          inputProps={{ min: 1, max: 99999 }}
          onChange={(e) => set('invoice_next_no', e.target.value)} />
      </Stack>
      <Typography variant="caption" sx={{ mt: -1.5, color: c.inkMuted }}>
        {t('settings.invoiceNextLabel', { label: nextLabel(
          v('invoice_prefix') || 'INV', d.invoice_next_label,
          form.invoice_next_no) })}
        {' '}{t('settings.invoicePrefixHint')}
      </Typography>

      <TextField label={t('settings.bankName')} value={v('bank_name')}
        disabled={!editable}
        onChange={(e) => set('bank_name', e.target.value)} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField label={t('settings.bankAccount')} value={v('bank_account')}
          disabled={!editable} sx={{ flex: 1 }}
          onChange={(e) => set('bank_account', e.target.value)} />
        <TextField label={t('settings.bankIban')} value={v('bank_iban')}
          disabled={!editable} sx={{ flex: 1.3 }}
          onChange={(e) => set('bank_iban', e.target.value)} />
      </Stack>

      {editable && (
        <Box><Button variant="contained" onClick={() => save.mutate()}
          disabled={save.isPending || !Object.keys(form).length}>
          {t('common.save')}</Button></Box>
      )}
    </Stack>
  )
}


/** What the next invoice will be called, as the user edits the fields.
 *
 * Built from the server's label (which knows the year) with the prefix and
 * number swapped for whatever is being typed, so the preview is never a
 * guess at the year. */
export function nextLabel(prefix: string, serverLabel: string | undefined,
                          typedNo: string | undefined): string {
  const parts = (serverLabel ?? '').split('/')
  const year = parts.length === 3 ? parts[1] : String(new Date().getFullYear())
  const n = typedNo !== undefined && typedNo !== ''
    ? Number(typedNo) : Number(parts[2] ?? 1)
  const safe = Number.isFinite(n) && n > 0 ? Math.floor(n) : 1
  return `${prefix.toUpperCase()}/${year}/${String(safe).padStart(5, '0')}`
}


// --- modules ---------------------------------------------------------------

/** What each module adds, in the order they are listed. `ready: false` is
 *  announced but cannot be switched on yet. */
const MODULES: { name: ModuleName; ready: boolean }[] = [
  { name: 'transport', ready: true },
  { name: 'inventory', ready: true },
]

/**
 * Switch whole areas of the product on or off for this company.
 *
 * The core books are the same for every company; a module adds an area
 * only some need -- trips and fleet for a haulier, stock and billing for
 * a trader. Off means gone for every user of this company: the menu, the
 * shortcuts, the dashboard card, and the server's endpoints. Nothing is
 * deleted: switching back on brings every trip back as it was.
 */
export function ModulesPanel({ editable }: { editable: boolean }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { me, refreshMe } = useAuth()
  const [confirm, setConfirm] = useState<ModuleName | null>(null)
  const [error, setError] = useState<string | null>(null)
  const on = modulesOf(me)
  // Only what this installation includes; the rest is not offered at all.
  const offered = MODULES.filter((m) => !me?.allowed_modules
    || me.allowed_modules.includes(m.name))

  const save = useMutation({
    mutationFn: (mods: string[]) => updateCompany({ modules: mods }),
    onSuccess: async () => {
      setError(null); setConfirm(null)
      void qc.invalidateQueries({ queryKey: ['company'] })
      // The menu reads the modules from the session: refresh it so the
      // Transport block appears or goes without a reload.
      await refreshMe()
    },
    onError: (e) => setError(errorMessage(e)),
  })

  const toggle = (name: ModuleName, next: boolean) => {
    if (!next) { setConfirm(name); return }          // off: ask first
    save.mutate([...on, name])
  }

  return (
    <Stack spacing={2} sx={{ maxWidth: 720 }}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      <Typography sx={{ fontSize: 14, color: c.inkSecondary }}>{t('modules.intro')}</Typography>
      {!editable && <Alert severity="info">{t('modules.adminOnly')}</Alert>}
      {offered.map((m) => {
        const active = on.includes(m.name)
        return (
          <Paper key={m.name} variant="outlined" sx={{
            p: 2, display: 'flex', gap: 2, alignItems: 'flex-start',
            borderColor: active ? c.primaryInk : undefined,
            opacity: m.ready ? 1 : 0.7 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography component="h3" sx={{ m: 0, fontSize: 15, fontWeight: 600 }}>
                  {t(`modules.${m.name}.name`)}</Typography>
                {!m.ready && <Chip size="small" label={t('modules.comingSoon')}
                  sx={{ height: 20, fontSize: 11 }} />}
                {active && <Chip size="small" color="primary" label={t('modules.on')}
                  sx={{ height: 20, fontSize: 11 }} />}
              </Stack>
              <Typography sx={{ fontSize: 13, color: c.inkMuted, mt: 0.5 }}>
                {t(`modules.${m.name}.desc`)}</Typography>
              {m.name === 'inventory' && active && (
                <NegativeStockSwitch editable={editable} />
              )}
            </Box>
            <Switch checked={active} disabled={!editable || !m.ready || save.isPending}
              onChange={(_, v) => toggle(m.name, v)}
              inputProps={{ 'aria-label': t(`modules.${m.name}.name`) }} />
          </Paper>
        )
      })}

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{confirm && t('modules.offConfirmTitle',
          { module: t(`modules.${confirm}.name`) })}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>{t('modules.offConfirm')}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setConfirm(null)}>{t('common.cancel')}</Button>
          <Button variant="contained" color="error" disabled={save.isPending}
            onClick={() => save.mutate(on.filter((x) => x !== confirm))}>
            {t('modules.switchOff')}</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}


/**
 * May a sale take a store below zero? Blocked by default: a shortfall is
 * usually a purchase nobody keyed, and blocking finds it. A company that
 * sells before the paperwork arrives turns it on and gets a warning on
 * each such sale instead.
 */
function NegativeStockSwitch({ editable }: { editable: boolean }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['company'], queryFn: fetchCompany })
  const save = useMutation({
    mutationFn: (v: boolean) => updateCompany({ allow_negative_stock: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['company'] }),
  })
  const on = !!q.data?.allow_negative_stock
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start"
      sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${c.divider}` }}>
      <Switch size="small" checked={on} disabled={!editable || !q.data || save.isPending}
        onChange={(_, v) => save.mutate(v)}
        inputProps={{ 'aria-label': t('modules.negativeStock') }} />
      <Box>
        <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>{t('modules.negativeStock')}</Typography>
        <Typography sx={{ fontSize: 12.5, color: c.inkMuted }}>
          {on ? t('modules.negativeStockOn') : t('modules.negativeStockOff')}</Typography>
      </Box>
    </Stack>
  )
}


// --- numbering -------------------------------------------------------------

function NumberingPanel({ editable, onDone }:
  { editable: boolean; onDone: () => void }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['policy'], queryFn: fetchPolicy })
  const [preview, setPreview] = useState<RenumberResult | null>(null)
  const [target, setTarget] = useState<GroupStyle | null>(null)
  const [error, setError] = useState<string | null>(null)

  const width = useMutation({
    mutationFn: (n: number) => updatePolicy({ ledger_serial_width: n }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['policy'] }),
    onError: (e) => setError(errorMessage(e)),
  })

  const apply = useMutation({
    mutationFn: () => renumberGroups(target!, false),
    onSuccess: () => {
      setPreview(null); setTarget(null)
      void qc.invalidateQueries({ queryKey: ['policy'] })
      onDone()
    },
    onError: (e) => setError(errorMessage(e)),
  })

  async function doPreview(style: GroupStyle) {
    setError(null)
    try {
      setTarget(style)
      setPreview(await renumberGroups(style, true))
    } catch (e) { setError(errorMessage(e)) }
  }

  if (q.isLoading) return <><Skeleton /><Skeleton /></>
  const p = q.data!

  return (
    <Stack spacing={2.5} sx={{ maxWidth: 620 }}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      <Box>
        <Box sx={{ mb: 1.5 }}><PanelHeading>{t('settings.groupStyle')}</PanelHeading></Box>
        <Stack spacing={1}>
          {STYLES.map((s) => (
            <Stack key={s.value} direction="row" alignItems="center" spacing={2}
              sx={{
                px: 1.5, py: 1, borderRadius: 1,
                border: `1px solid ${p.group_style === s.value ? c.primaryInk : c.border}`,
                bgcolor: p.group_style === s.value ? c.primarySoft : undefined,
              }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 500, width: 110 }}>
                {t(`settings.style${s.value}`)}
              </Typography>
              <span className="num" style={{ fontSize: 12.5, color: c.inkMuted,
                flex: 1, textAlign: 'start' }}>{s.example}</span>
              {p.group_style === s.value ? (
                <Chip size="small" label={t('settings.current')} />
              ) : editable ? (
                <Button size="small" onClick={() => doPreview(s.value)}>
                  {t('settings.switchTo')}
                </Button>
              ) : null}
            </Stack>
          ))}
        </Stack>
        <Typography variant="caption" color="text.secondary"
          sx={{ mt: 1, display: 'block' }}>
          {t('settings.styleHint')}
        </Typography>
      </Box>

      <TextField select label={t('settings.serialWidth')}
        value={p.ledger_serial_width} disabled={!editable} sx={{ width: 220 }}
        onChange={(e) => width.mutate(Number(e.target.value))}
        helperText={t('settings.serialHint', {
          example: `D${'0'.repeat(p.ledger_serial_width - 1)}1` })}>
        {[3, 4, 5, 6].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
      </TextField>

      <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('settings.renumberTitle')}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('settings.renumberSafe')}
          </Alert>
          <Box className="ruled" sx={{
            maxHeight: 300, overflowY: 'auto',
            border: `1px solid ${c.border}`, borderRadius: 1 }}>
            {preview?.changes.map((ch) => (
              <Stack key={ch.id} direction="row" spacing={1.5}
                sx={{ px: 1.5, py: 0.75 }} alignItems="center">
                <Typography sx={{ fontSize: 12.5, flex: 1 }} noWrap>
                  {ch.name}</Typography>
                <span className="num" style={{ fontSize: 11.5, color: c.inkMuted }}>
                  {ch.old}</span>
                <Typography sx={{ color: c.inkMuted }}>→</Typography>
                <span className="num" style={{ fontSize: 11.5, color: c.primaryInk }}>
                  {ch.new}</span>
              </Stack>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setPreview(null)}>
            {t('common.cancel')}</Button>
          <Button variant="contained" onClick={() => apply.mutate()}
            disabled={apply.isPending}>
            {t('settings.applyRenumber', { n: preview?.count ?? 0 })}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

// --- users -----------------------------------------------------------------

function UsersPanel() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { me } = useAuth()
  const [dialog, setDialog] = useState<{ id?: number } | null>(null)
  const q = useQuery({ queryKey: ['staff'], queryFn: fetchStaff })
  const companyName = (id: number) =>
    me?.companies.find((x) => x.id === id)?.name_en ?? `#${id}`
  const multi = (me?.companies.length ?? 0) > 1

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
        <Button variant="contained" size="small" onClick={() => setDialog({})}>
          {t('settings.newUser')}
        </Button>
      </Stack>
      {q.isLoading ? <><Skeleton /><Skeleton /></> : (
        <Box className="ruled" sx={{ border: `1px solid ${c.border}`, borderRadius: 1 }}>
          {q.data?.map((u) => (
            <Stack key={u.id} direction="row" alignItems="center" spacing={1.5}
              onClick={() => setDialog({ id: u.id })}
              sx={{ px: 1.5, py: 1.1, cursor: 'pointer',
                    '&:hover': { bgcolor: c.primarySoft } }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13.5 }} noWrap>
                  {u.full_name || u.username}</Typography>
                <Typography sx={{ fontSize: 11.5, color: c.inkMuted }} noWrap>
                  {u.username} · {u.permissions.length} {t('settings.permissions')}
                  {/* Which companies, said in words: with more than one,
                      "can this person see the Logistics books?" is the
                      question this list gets opened to answer. */}
                  {multi && u.company_ids?.length > 0 && (
                    <> · {u.company_ids.map(companyName).join(', ')}</>
                  )}
                </Typography>
              </Box>
              {u.restrict_to_own && (
                <Chip size="small" variant="outlined"
                  label={t('settings.ownOnly')} sx={{ height: 20, fontSize: 10 }} />
              )}
              {u.allowed_cash_bank.length > 0 && (
                <Chip size="small" variant="outlined"
                  label={u.allowed_cash_bank.join(', ')}
                  sx={{ height: 20, fontSize: 10 }} />
              )}
              <Chip size="small" label={u.role} sx={{ height: 20, fontSize: 10 }} />
            </Stack>
          ))}
        </Box>
      )}
      {dialog && (
        <StaffDialog userId={dialog.id} onClose={() => setDialog(null)}
          onSaved={() => qc.invalidateQueries({ queryKey: ['staff'] })} />
      )}
    </>
  )
}

// --- periods ---------------------------------------------------------------

function PeriodsPanel() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['years'], queryFn: fetchYears })
  const [error, setError] = useState<string | null>(null)

  const toggle = useMutation({
    mutationFn: ({ year, lock }: { year: number; lock: boolean }) =>
      lockYear(year, lock),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['years'] }),
    onError: (e) => setError(errorMessage(e)),
  })

  if (q.isLoading) return <><Skeleton /><Skeleton /></>

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      <Alert severity="info">{t('settings.lockHint')}</Alert>
      <Box className="ruled" sx={{ border: `1px solid ${c.border}`, borderRadius: 1 }}>
        {q.data?.map((y) => (
          <Stack key={y.id} direction="row" alignItems="center" spacing={1.5}
            sx={{ px: 1.5, py: 1.1 }}>
            <span className="num" style={{ fontSize: 13.5, fontWeight: 600,
              minWidth: 54, textAlign: 'start' }}>{y.year_code}</span>
            <Typography sx={{ fontSize: 12.5, color: c.inkMuted, flex: 1 }}>
              {y.start_date} → {y.end_date} · {y.voucher_count}{' '}
              {t('settings.vouchers')}
            </Typography>
            {y.is_current && <Chip size="small" label={t('settings.current')}
              sx={{ height: 20, fontSize: 10 }} />}
            <Button size="small"
              startIcon={y.is_locked ? <LockIcon /> : <LockOpenIcon />}
              color={y.is_locked ? 'error' : 'inherit'}
              onClick={() => toggle.mutate({ year: y.year_code, lock: !y.is_locked })}>
              {y.is_locked ? t('settings.locked') : t('settings.open')}
            </Button>
          </Stack>
        ))}
      </Box>
    </Stack>
  )
}
